/**
 * Halloween recipe cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenRecipeCardsPack: ToilePack = {
    slug: 'halloween-recipe-cards',
    category: 'season',
    name: 'Halloween recipe cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'soup', label: 'Pumpkin soup', subject: 'A vertical recipe card lettered "Pumpkin Soup" with "Ingredients" and "Method" headers and blank lines, a pumpkin motif'},
        {key: 'punch', label: "Witch's punch", subject: 'A vertical recipe card lettered "Witch\'s Punch" with blank lines and a bubbling cauldron motif'},
        {key: 'snacks', label: 'Spooky snacks', subject: 'A vertical recipe card lettered "Spooky Snacks" with blank lines and a bat-and-candy border'},
    ],
    styles: [
        {key: 'classic', label: 'Classic', style: 'classic halloween printable, bat and pumpkin silhouettes, playful spooky lettering, print-ready', palette: 'Orange, black and white', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'gothic', label: 'Gothic', style: 'gothic halloween printable, ornate filigree frames, elegant serif lettering, print-ready', palette: 'Black, deep purple, bone white and a little gold', framing: 'Laid out on a dark page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' halloween printable, friendly cartoon monsters, rounded lettering, print-ready", palette: 'Bright orange, purple, lime green and white', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for a potluck', 'as a 4 by 6 card', 'with a nutrition line'],
    directionHint: 'try: as a 4 by 6 card',
    seo: {
        title: 'Halloween recipe cards: soup, punch and snacks',
        metaDescription: "Nine Halloween recipe cards — pumpkin soup, witch's punch, spooky snacks — headers lettered, lines blank, classic, gothic or kids' style.",
        h1: 'Halloween recipe cards: soup, punch and snacks in three styles',
        tagline: 'Nine cards, three recipe themes, three styles each — header lettered, lines left blank for your own recipe.',
        intro: "Three Halloween recipe cards to fill in by hand: a pumpkin soup card, a witch's punch card, a spooky snack card. Each has its header already lettered, with ruled lines left blank for the ingredients and steps you write in. Drawn classic orange-and-black, gothic elegant, or bright kids' style. Type a different dish and the header redraws.",
        useCases: [
            'a Halloween party recipe box or binder',
            'a potluck recipe-sharing handout',
            'a cooking activity card for kids',
            'a keepsake family recipe card',
        ],
        faq: [
            {q: 'Are the recipes already written on the card?', a: 'No — the header names the dish, but the lines are left blank so you write in your own ingredients and steps.'},
            {q: 'What size are the cards?', a: 'Each card is drawn at a standard recipe-card proportion, ready to print, cut, and file or laminate.'},
            {q: 'Can I change the dish name on the header?', a: "Yes — type your own dish, like 'Mummy Dogs' or 'Candy Corn Bark,' and the header redraws in the same style."},
            {q: "Is there a simpler version for a kids' cooking activity?", a: "The kids' set uses larger lines and bright icons, better suited to a child filling one in themselves."},
        ],
    },
}
